import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { attachOffline } from "./offline";
import { toast } from "sonner";

// ---- Shared tokens, enums, context and primitives for every Admissions-CRM screen ----
export const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/rcrm`;
export const T = { ink: "#1A1A2E", muted: "#6B7280", sky: "#C2410C", skyDeep: "#9A3412", gold: "#CA8A04",
  teal: "#1F9D75", coral: "#D64545", indigo: "#B45309", nav: "#7C2D12", line: "#FDEBDD", surface: "#FBF7F3" };
export const STAGES = ["New", "Contacted", "Qualified", "Appointment", "Visited",
  "Counselled", "Seat blocked", "Admitted", "Fee paid"];
export const TERMINAL_STAGES = ["Lost", "Parked (OC)", "DNC"];
export const CLOSED_STAGES = ["Admitted", "Fee paid", "Lost", "Parked (OC)", "DNC"];
export const LOST_REASONS = ["Fee", "Distance", "Other institute", "Decide later", "Child unwilling",
  "Already has tuition", "Wants online", "Timing/batch", "No response after 7 attempts",
  "Wrong data", "Moved out of Nagpur", "Other"];
export const OUTCOMES = [["SP", "Spoke to parent"], ["SS", "Spoke to student"], ["H", "Hung up / cut"],
  ["NA", "No answer"], ["WN", "Wrong / invalid number"], ["OOS", "Out of service"], ["OC", "Admitted elsewhere"], ["NI", "Not interested"],
  ["CB", "Callback fixed"], ["TK", "Appointment fixed"], ["STOP", "Don't call"]];
export const DATETIME_OUTCOMES = ["CB", "TK"];
export const STAGE_COLORS = {
  "New": "#64748B", "Contacted": "#0EA5E9", "Qualified": "#6366F1",
  "Appointment": "#8B5CF6", "Visited": "#A855F7", "Counselled": "#F59E0B",
  "Seat blocked": "#0D9488", "Admitted": "#16A34A", "Fee paid": "#059669",
  "Lost": "#DC2626", "Parked (OC)": "#B45309", "DNC": "#4B5563",
};
export const stageColor = (s) => STAGE_COLORS[s] || T.muted;
export const inr = (n) => "₹" + (n || 0).toLocaleString("en-IN");
export const ALL_STAGES = [...STAGES, ...TERMINAL_STAGES];
export const CEO_ROLES = ["Director"];
export const COUNSELLING_ROLES = ["Director", "Co-Director", "Floor Lead", "Counsellor"];
export const COUNSELLING_STAGES = ["Appointment", "Visited", "Counselled", "Seat blocked"];
export const StageLegend = () => (
  <div data-testid="rcrm-stage-legend" style={{ display: "flex", flexWrap: "wrap", gap: "6px 12px", margin: "0 0 12px", padding: "8px 10px", background: T.surface, borderRadius: 8, border: `1px solid ${T.line}` }}>
    {ALL_STAGES.map((s) => (
      <span key={s} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: T.ink }}>
        <span style={{ width: 10, height: 10, borderRadius: 3, background: stageColor(s), display: "inline-block" }} />{s}
      </span>
    ))}
  </div>
);

export const BAND_COLORS = { HOT: "#DC2626", WARM: "#EA580C", NURTURE: "#0EA5E9", LOW: "#94A3B8", EXCLUDED: "#4B5563" };
export const BandTag = ({ band, score }) => {
  if (!band) return <span style={{ color: T.muted, fontSize: 11 }}>—</span>;
  const c = BAND_COLORS[band] || T.muted;
  return <span data-testid={`rcrm-band-${band}`} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, color: c, background: c + "18", borderRadius: 10, padding: "2px 8px" }}>{band}{score != null && band !== "EXCLUDED" ? ` · ${score}` : ""}</span>;
};

export const Ctx = createContext(null);
export const useCrm = () => useContext(Ctx);
export function api(token) {
  const i = axios.create({ baseURL: BASE, headers: token ? { Authorization: `Bearer ${token}` } : {} });
  return attachOffline(i);
}

// ---- style primitives ----
export const lbl = { fontSize: 12, color: T.muted, display: "block", margin: "0 0 4px" };
export const inp = { width: "100%", padding: "9px 11px", border: `1px solid ${T.line}`, borderRadius: 8, fontSize: 14, marginBottom: 12, outline: "none", background: "#fff", boxSizing: "border-box" };
export const btn = (bg) => ({ background: bg, color: "#fff", border: "none", padding: "9px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" });
export const chip = { background: "#FFF3E9", color: T.skyDeep, border: `1px solid #FDEBDD`, padding: "6px 10px", borderRadius: 20, fontSize: 12, cursor: "pointer" };
export const miniTag = { background: T.surface, color: T.skyDeep, border: `1px solid ${T.line}`, padding: "1px 7px", borderRadius: 10, fontSize: 10, fontWeight: 600 };
export const h1 = { fontSize: 24, fontWeight: 700, margin: "0 0 14px" };
export const cap = { fontSize: 13, color: T.ink };

// ---- layout primitives ----
export const Modal = ({ children, onClose, title, testid }) => (
  <div style={{ position: "fixed", inset: 0, zIndex: 60, display: "grid", placeItems: "center" }} onClick={onClose}>
    <div style={{ position: "absolute", inset: 0, background: "rgba(20,33,61,0.35)" }} />
    <div onClick={(e) => e.stopPropagation()} data-testid={testid} style={{ position: "relative", background: "#fff", borderRadius: 12, padding: 22, width: 440, maxWidth: "94%", maxHeight: "90vh", overflowY: "auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}><h2 style={{ margin: 0, fontSize: 18 }}>{title}</h2><button onClick={onClose} style={{ border: "none", background: "none", fontSize: 20, cursor: "pointer" }}>×</button></div>
      {children}
    </div>
  </div>
);
export const Drawer = ({ children, onClose, title, testid }) => (
  <div style={{ position: "fixed", inset: 0, zIndex: 50 }} onClick={onClose}>
    <div style={{ position: "absolute", inset: 0, background: "rgba(20,33,61,0.35)" }} />
    <div onClick={(e) => e.stopPropagation()} data-testid={testid} style={{ position: "absolute", right: 0, top: 0, height: "100%", width: 560, maxWidth: "100%", background: "#fff", overflowY: "auto", padding: 22 }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}><h2 style={{ margin: 0, fontSize: 18 }}>{title}</h2><button onClick={onClose} style={{ border: "none", background: "none", fontSize: 20, cursor: "pointer" }}>×</button></div>
      <div style={{ marginTop: 14 }}>{children}</div>
    </div>
  </div>
);
export const MiniStat = ({ label, value }) => <div style={{ background: T.surface, padding: "8px 10px", borderRadius: 8 }}><div style={{ fontSize: 11, color: T.muted }}>{label}</div><b style={{ fontSize: 16 }}>{value}</b></div>;
export const Card = ({ children, pad = 16 }) => <div style={{ background: "#fff", border: `1px solid ${T.line}`, borderRadius: 12, padding: pad }}>{children}</div>;
export const Kpi = ({ label, value, color }) => <Card><div style={{ fontSize: 12, color: T.muted }}>{label}</div><div style={{ fontSize: 26, fontWeight: 700, color }}>{value}</div></Card>;
export const Tag = ({ s }) => <span style={{ background: stageColor(s) + "22", color: stageColor(s), padding: "3px 9px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{s}</span>;
export const KV = ({ k, v }) => <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: `1px solid ${T.line}` }}><span style={{ color: T.muted, fontSize: 13 }}>{k}</span><b style={{ fontSize: 13 }}>{v}</b></div>;
export const Loading = () => <div style={{ padding: 40, textAlign: "center", color: T.muted }}>Loading…</div>;
// Board-tab move announcement: any lead action response may carry tab_move {to_label, student_name, up}
export const announceTabMove = (data) => {
  const m = data && data.tab_move;
  if (!m) return false;
  const who = m.student_name ? `${m.student_name} ` : "";
  toast[m.up ? "success" : "info"](`${who}moved to ${m.to_label}`, { description: m.up ? `Progress: ${m.from_label} → ${m.to_label}` : `${m.from_label} → ${m.to_label}`, id: `tabmove-${m.student_name}-${m.to}` });
  return true;
};
export const Empty = ({ msg }) => <Card><div style={{ padding: 20, textAlign: "center", color: T.muted }}>{msg}</div></Card>;
export const th = { padding: "10px 12px", fontWeight: 600 };
export const td = { padding: "9px 12px" };
export const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—");


// Search any lead by phone number (or name). Reuses GET /leads?q= which is already
// permission-scoped per role. onPick(leadId) opens the caller's lead drawer with full info.
export function LeadSearch({ onPick, placeholder = "Search by phone or name…", width = 260 }) {
  const { api } = useCrm();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 3) { setRes(null); setOpen(false); return; }
    const t = setTimeout(() => {
      api.get("/leads", { params: { q: term } })
        .then((r) => { setRes(r.data.items || []); setOpen(true); })
        .catch(() => { setRes([]); setOpen(true); });
    }, 350);
    return () => clearTimeout(t);
  }, [q, api]);
  const pick = (id) => { onPick(id); setOpen(false); setQ(""); setRes(null); };
  return (
    <div style={{ position: "relative", width }} data-testid="rcrm-lead-search">
      <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => res && setOpen(true)}
        placeholder={placeholder} style={{ ...inp, marginBottom: 0, width: "100%" }} data-testid="rcrm-lead-search-input" />
      {open && res && (
        <div data-testid="rcrm-lead-search-results" onMouseLeave={() => setOpen(false)}
          style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 70, background: "#fff", border: `1px solid ${T.line}`, borderRadius: 8, marginTop: 4, maxHeight: 340, overflow: "auto", boxShadow: "0 8px 24px rgba(0,0,0,.14)" }}>
          {res.length === 0 ? (
            <div style={{ padding: 12, fontSize: 13, color: T.muted }}>No lead found for &ldquo;{q.trim()}&rdquo;.</div>
          ) : res.slice(0, 25).map((l) => (
            <button key={l.id} data-testid={`rcrm-lead-search-result-${l.id}`} onClick={() => pick(l.id)}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 12px", border: "none", borderBottom: `1px solid ${T.line}`, background: "#fff", cursor: "pointer" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>{l.student_name}</div>
              <div style={{ fontSize: 12, color: T.muted }}>{[l.phone, l.stage, l.class && `Class ${l.class}`].filter(Boolean).join(" · ")}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// Guided stage changer for a selected lead: book home/centre counselling, mark
// Counselled, or record Admissions done. Reuses production endpoints + their gates.
export function StagePanel({ leadId, stage, onChanged }) {
  const { api } = useCrm();
  const [admit, setAdmit] = useState(false);
  const [adForm, setAdForm] = useState({ form_signed_date: new Date().toISOString().slice(0, 10), fee_plan: 1 });
  const [busy, setBusy] = useState(false);
  if (["Lost", "Parked (OC)", "DNC"].includes(stage)) return null;
  const pill = (color, active) => ({ padding: "8px 12px", borderRadius: 20, fontSize: 12.5, fontWeight: 600,
    cursor: busy ? "wait" : "pointer", border: `1px solid ${color}`, background: active ? color : "#fff", color: active ? "#fff" : color });
  const book = async (type) => {
    const v = window.prompt(`${type === "Home" ? "Home counselling" : "Centre visit"} date & time (YYYY-MM-DD HH:MM)`, "");
    if (!v) return;
    const d = new Date(v.replace(" ", "T"));
    if (isNaN(d.getTime())) { toast.error("Enter a valid date & time"); return; }
    setBusy(true);
    try {
      await api.post(`/leads/${leadId}/call`, { outcome: "TK", appointment_type: type, followup_at: d.toISOString() });
      try {
        await api.post(`/leads/${leadId}/stage`, { stage: "Appointment" });
      } catch (e2) {
        const msg = e2.response?.data?.detail || "";
        if (!/already|appointment/i.test(msg)) toast.error(`Booked, but stage not moved: ${msg || "please check"}`);
      }
      toast.success(`${type === "Home" ? "Home counselling" : "Centre visit"} booked`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not book"); } finally { setBusy(false); }
  };
  const move = async (s, extra = {}) => {
    setBusy(true);
    try { await api.post(`/leads/${leadId}/stage`, { stage: s, ...extra }); toast.success(`Moved to ${s}`); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not change stage"); } finally { setBusy(false); }
  };
  const doAdmit = async () => {
    if (!adForm.form_signed_date) { toast.error("Pick the form signed date"); return; }
    await move("Admitted", { form_signed_date: adForm.form_signed_date, fee_plan: Number(adForm.fee_plan) });
    setAdmit(false);
  };
  const rtse = async () => {
    const roll = window.prompt("RTSE roll number");
    if (!roll || !roll.trim()) return;
    const scoreStr = window.prompt("RTSE score (optional)", "");
    const score = scoreStr && !isNaN(Number(scoreStr)) ? Number(scoreStr) : null;
    const at = new Date(Date.now() + 86400000).toISOString().slice(0, 16);
    setBusy(true);
    try { await api.post(`/myleads/${leadId}/rtse`, { roll_number: roll.trim(), score, next_action: "Counselling", next_action_at: at }); toast.success("RTSE marked completed"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not mark RTSE"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="rcrm-stage-panel" style={{ marginBottom: 14, padding: 12, background: T.surface, borderRadius: 10, border: `1px solid ${T.line}` }}>
      <b style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: T.muted }}>Change stage</b>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
        <button disabled={busy} data-testid="rcrm-stage-home" onClick={() => book("Home")} style={pill(T.indigo, stage === "Appointment")}>Home counselling booked</button>
        <button disabled={busy} data-testid="rcrm-stage-centre" onClick={() => book("Centre")} style={pill(T.sky, false)}>Centre visit booked</button>
        <button disabled={busy} data-testid="rcrm-stage-counselled" onClick={() => move("Counselled")} style={pill(T.gold, stage === "Counselled")}>Counselled</button>
        <button disabled={busy} data-testid="rcrm-stage-rtse" onClick={rtse} style={pill(T.coral, false)}>RTSE completed</button>
        <button disabled={busy} data-testid="rcrm-stage-admitted" onClick={() => setAdmit((a) => !a)} style={pill(T.teal, stage === "Admitted")}>Admissions done</button>
      </div>
      {admit && (
        <div data-testid="rcrm-stage-admit-form" style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div><label style={{ fontSize: 11, color: T.muted, display: "block", marginBottom: 2 }}>Form signed date</label>
            <input type="date" value={adForm.form_signed_date} onChange={(e) => setAdForm((s) => ({ ...s, form_signed_date: e.target.value }))} style={{ ...inp, marginBottom: 0 }} data-testid="rcrm-stage-admit-date" /></div>
          <div><label style={{ fontSize: 11, color: T.muted, display: "block", marginBottom: 2 }}>Fee plan</label>
            <select value={adForm.fee_plan} onChange={(e) => setAdForm((s) => ({ ...s, fee_plan: e.target.value }))} style={{ ...inp, marginBottom: 0 }} data-testid="rcrm-stage-admit-plan">
              <option value={1}>1 instalment</option><option value={2}>2 instalments</option><option value={3}>3 instalments</option></select></div>
          <button disabled={busy} onClick={doAdmit} style={btn(T.teal)} data-testid="rcrm-stage-admit-confirm">Confirm admission</button>
        </div>
      )}
    </div>
  );
}
